import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { statsData, features } from '@/mocks/testimonials';

const faqs = [
  {
    q: '韩国漫画网的漫画都是免费的吗？',
    a: '是的，韩国漫画网上所有漫画均支持免费在线阅读，用户无需支付任何费用即可享受高质量的韩漫内容。我们致力于为广大韩漫爱好者打造一个开放、免费的阅读平台。',
  },
  {
    q: '漫画更新频率如何？',
    a: '韩国漫画网每日更新超过200话热门韩国漫画，与韩国官方保持高度同步。热门连载漫画每周至少更新1-2话，确保读者能够第一时间追到最新剧情。',
  },
  {
    q: '支持哪些设备阅读？',
    a: '韩国漫画网采用响应式网页设计，完美适配电脑、平板和手机等各类设备。无论您使用什么设备，都能获得流畅舒适的阅读体验。您可以直接在浏览器中打开网站开始阅读。',
  },
  {
    q: '需要注册账号才能看漫画吗？',
    a: '不需要。韩国漫画网无需注册即可直接浏览和阅读所有漫画内容。如果您想收藏喜欢的漫画或记录阅读进度，可以注册账号享受更多个性化功能。',
  },
];

const brandValues = [
  { icon: 'ri-heart-line', title: '热爱漫画', desc: '我们是一群热爱韩国漫画的读者，因为热爱而创建了这个平台。每一部收录的作品都经过了团队的精心挑选，确保为读者提供最优质的韩漫内容。' },
  { icon: 'ri-shield-check-line', title: '品质保障', desc: '坚持高清画质和精准翻译，所有漫画均经过多轮校对。我们拒绝低质量机翻，致力于让每一位读者都能获得原汁原味的阅读体验。' },
  { icon: 'ri-flashlight-line', title: '极速更新', desc: '与韩国官方保持高度同步，热门漫画实现同步更新。专业的翻译团队7×24小时待命，确保第一时间为读者呈现最新话内容。' },
  { icon: 'ri-user-heart-line', title: '用户至上', desc: '始终以读者体验为核心，界面清爽无广告干扰。认真倾听每一位用户的反馈建议，持续优化产品功能，打造最舒适的韩漫阅读环境。' },
];

const milestones = [
  { year: '2022', event: '韩国漫画网正式上线，收录首批1000部韩漫' },
  { year: '2023', event: '注册用户突破100万，日活用户超30万' },
  { year: '2024', event: '漫画库扩充至5000+部，实现每日同步更新' },
  { year: '2025', event: '活跃读者突破500万，成为国内最大韩漫平台' },
  { year: '2026', event: '收录漫画突破10000部，全面升级阅读体验' },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 品牌介绍与联系方式 - 韩国漫画网';
  }, []);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
            <a href="/" className="hover:text-primary-500 transition-colors">首页</a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </nav>

          <section className="mb-16 md:mb-20">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium mb-4">
                About Us
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-black text-foreground-950 mb-4">
                关于韩国漫画网
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                韩国漫画网成立于2022年，是专注于韩国漫画在线阅读的专业平台。
                我们致力于为华语地区的韩漫爱好者提供最全面、最优质的韩漫内容，
                打造一个没有广告干扰、操作简单、更新迅速的舒适阅读空间。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {brandValues.map((v) => (
                <div key={v.title} className="bg-background-100 rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100">
                    <i className={`${v.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <div className="bg-accent-100/60 rounded-3xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-accent-300 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-8">
                  {milestones.map((m, i) => (
                    <div key={m.year} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="md:w-1/2"></div>
                      <div className="absolute left-0 md:left-1/2 w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center -translate-x-1/2 hidden md:flex">
                        <span className="w-2 h-2 rounded-full bg-background-50"></span>
                      </div>
                      <div className="md:w-1/2 bg-background-50 rounded-xl p-5">
                        <span className="text-xs font-label font-bold text-accent-600">{m.year}</span>
                        <p className="text-sm text-foreground-700 mt-1">{m.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              平台数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {statsData.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-2xl p-6 text-center">
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-3xl md:text-4xl font-heading font-black text-primary-600">{stat.value}</span>
                    <span className="text-base text-foreground-500 font-label">{stat.suffix}</span>
                  </div>
                  <p className="text-sm text-foreground-500 font-label">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
              {features.map((f) => (
                <span key={f} className="flex items-center gap-1.5 text-sm text-foreground-600">
                  <i className="ri-check-line text-accent-500"></i>{f}
                </span>
              ))}
            </div>
          </section>

          <section className="mb-16 md:mb-20" id="contact">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-mail-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-950 mb-2">商务合作</h3>
                <p className="text-xs text-foreground-500 mb-2">广告投放、品牌合作、版权合作</p>
                <a href="mailto:hello@szfirstteam.com" className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium">
                  hello@szfirstteam.com
                </a>
              </div>
              <div className="bg-background-100 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-user-voice-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-950 mb-2">内容合作</h3>
                <p className="text-xs text-foreground-500 mb-2">翻译组合作、内容投稿、版权授权</p>
                <a href="mailto:hello@szfirstteam.com" className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium">
                  hello@szfirstteam.com
                </a>
              </div>
              <div className="bg-background-100 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-question-answer-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-950 mb-2">用户反馈</h3>
                <p className="text-xs text-foreground-500 mb-2">意见反馈、问题报告、功能建议</p>
                <a href="mailto:hello@szfirstteam.com" className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium">
                  hello@szfirstteam.com
                </a>
              </div>
            </div>
          </section>

          <section className="pb-16 md:pb-20">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm font-label font-medium text-foreground-950 pr-4">
                      <strong className="text-primary-600 mr-2">Q{i + 1}.</strong>
                      {faq.q}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于韩国漫画网",
          "description": "韩国漫画网成立于2022年，专注于韩国漫画在线阅读的专业平台，提供最全面优质的韩漫内容。",
          "url": "http://szfirstteam.com/about",
          "isPartOf": {
            "@type": "WebSite",
            "name": "韩国漫画网",
            "url": "http://szfirstteam.com/"
          },
          "about": {
            "@type": "Organization",
            "name": "韩国漫画网",
            "foundingDate": "2022",
            "description": "韩国漫画网是专注于韩国漫画在线阅读的专业平台",
            "url": "http://szfirstteam.com/",
            "contactPoint": [
              { "@type": "ContactPoint", "contactType": "business cooperation", "email": "hello@szfirstteam.com" },
              { "@type": "ContactPoint", "contactType": "content cooperation", "email": "hello@szfirstteam.com" },
              { "@type": "ContactPoint", "contactType": "customer support", "email": "hello@szfirstteam.com" }
            ]
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://szfirstteam.com/" },
              { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "http://szfirstteam.com/about" }
            ]
          }
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((faq, i) => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.a
            }
          }))
        })}
      </script>
    </>
  );
}